import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import isAfter from 'date-fns/is_after';

import { Text } from '@zillow/constellation';

import { RMWContext } from 'app/shared/providers/RMWContext';
import StringConstants from 'app/shared/constants/StringConstants';

import TextLinkContent from './TextLinkContent';
import DuplicateListingDetailsContainer from './DuplicateListingDetailsContainer';
import { StyledList } from './StyledComponents';

const { FORMATTED_BRAND_NAMES: brandConstants } = StringConstants;
const DEMOTED_LISTING_COPY = 'We found a higher quality version of this listing in your account.' +
    ' Any edits made to this version may not be seen by renters on Zillow, Trulia and HotPads. ';
const compareDates = (first, second) => {
    const firstDate = new Date(first.dateUpdated);
    const secondDate = new Date(second.dateUpdated);
    const isSecondMoreRecent = isAfter(secondDate, firstDate);

    return isSecondMoreRecent ? 1 : -1;
};

const propTypes = {
    isFeed: PropTypes.bool.isRequired,
    topDupesDetails: PropTypes.object.isRequired,
};

const DuplicateListingDetails = ({
    isFeed,
    topDupesDetails: {
        isTopDupe,
        dupes,
        additionalListingSources,
        topDupeListingId,
    },
}) => {
    const { paths, brand } = useContext(RMWContext);
    const isSingleSource = additionalListingSources.length === 1;
    const manualDupeToShow = isFeed && dupes.sort(compareDates).find(dupe => dupe.feedName === brandConstants[brand]
        || dupe.feedName === 'Zillow Rental Manager');

    if (!isTopDupe) {
        return (
            <DuplicateListingDetailsContainer>
                <TextLinkContent
                    text={DEMOTED_LISTING_COPY}
                    href={`${paths.propertiesPage}/${topDupeListingId}/${paths.propertyDetailsListing}`}
                    cta="View higher quality listing."
                />
            </DuplicateListingDetailsContainer>
        );
    }

    if (isFeed) {
        if (!manualDupeToShow) {
            return null;
        }

        return (
            <DuplicateListingDetailsContainer>
                <TextLinkContent
                    text="Edit your "
                    href={`${paths.propertiesPage}/${manualDupeToShow.listingId}/${paths.propertyDetailsListing}`}
                    cta={`${StringConstants.FORMATTED_BRAND_NAMES_RENTAL[brand]} listing.`}
                />
            </DuplicateListingDetailsContainer>
        );
    }

    // If on manual listing, show list of feed sources
    if (additionalListingSources.length !== 0) {
        return (
            <DuplicateListingDetailsContainer>
                <Text fontType="body" data-test="not-posted">
                    {`There ${isSingleSource ? 'is an alternative version' : 'are alternative versions'} of
                    this listing being received from the following source${isSingleSource ? '' : 's'},
                    but ${isSingleSource ? 'that version is' : 'those versions are'} not currently posted:`}
                </Text>
                <StyledList>
                    {additionalListingSources.sort().map((source, i) => <li key={i}>{source}</li>)}
                </StyledList>
            </DuplicateListingDetailsContainer>
        );
    }

    return null;
};

DuplicateListingDetails.propTypes = propTypes;

export default DuplicateListingDetails;
